import { Monitor, FileText, Package, MessageSquare } from 'lucide-react';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { useTilt } from '@/hooks/useEffects';

const SERVICES = [
  {
    icon: Monitor,
    title: 'Website Design',
    description: 'Full custom website design tailored to your brand. Clean, modern, and built to make your business stand out from the competition.',
    tag: 'Most Popular',
  },
  {
    icon: FileText,
    title: 'Landing Pages',
    description: 'High-converting landing pages that turn visitors into customers. Perfect for campaigns, product launches, or lead generation.',
    tag: null,
  },
  {
    icon: Package,
    title: 'Small Business Packages',
    description: 'Complete website and social presence solutions for businesses getting started online. Website, social media setup, and guidance to help you grow.',
    tag: null,
  },
  {
    icon: MessageSquare,
    title: 'Online Presence Review',
    description: 'Expert advice on improving your digital presence. Get actionable insights on what is working and what could be better.',
    tag: null,
  },
];

function ServiceCard({ service, i }) {
  const tilt = useTilt(4);
  return (
    <ScrollReveal delay={i * 100}>
      <div
        data-testid={`service-card-${i}`}
        ref={tilt.ref}
        onMouseMove={tilt.onMouseMove}
        onMouseLeave={tilt.onMouseLeave}
        className="tilt-card group relative bg-white dark:bg-[#111111] border border-slate-200 dark:border-white/10 rounded-2xl p-8 md:p-10 glow-hover overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-[2px] bg-[#00E5FF] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

        <div className="flex items-start justify-between mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#F8F9FA] dark:bg-white/5 border border-slate-100 dark:border-white/5 flex items-center justify-center group-hover:bg-[#00E5FF]/10 group-hover:border-[#00E5FF]/20 transition-colors">
            <service.icon className="w-5 h-5 text-slate-600 dark:text-slate-400 group-hover:text-[#00B4D8] transition-colors" />
          </div>
          {service.tag && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#00B4D8] bg-[#00E5FF]/10 px-3 py-1 rounded-full">
              {service.tag}
            </span>
          )}
        </div>

        <h3 className="font-['Outfit'] text-xl sm:text-2xl font-semibold tracking-tight text-[#0A0A0A] dark:text-white mb-3">
          {service.title}
        </h3>
        <p className="text-sm sm:text-base leading-relaxed text-slate-500 dark:text-slate-400">
          {service.description}
        </p>
      </div>
    </ScrollReveal>
  );
}

export default function ServicesSection() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 gradient-line w-1/3" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-400 block mb-4">
            What I Do
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-4 max-w-lg">
            Services Built Around Your Goals
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400 mb-16 max-w-lg">
            Every project is different. Here is how I can help your business grow online.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {SERVICES.map((service, i) => (
            <ServiceCard key={service.title} service={service} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
